/**
 * Wizarding-world pricing.
 *
 * Internal store: integer Knuts.
 * 1 Galleon = 17 Sickles = 493 Knuts (PLAN-v2 §5).
 *
 * `formatKnuts(n)` is the only public entry point.
 */

export const KNUTS_PER_SICKLE = 29;
export const SICKLES_PER_GALLEON = 17;
export const KNUTS_PER_GALLEON = KNUTS_PER_SICKLE * SICKLES_PER_GALLEON; // 493

export interface FormatOpts {
  /** If true, returns "~7G" using only the largest non-zero unit. */
  compact?: boolean;
}

function assertNonNegInteger(n: number): void {
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    throw new TypeError(`expected finite number, got ${String(n)}`);
  }
  if (!Number.isInteger(n)) {
    throw new RangeError(`expected integer Knuts, got ${n}`);
  }
  if (n < 0) {
    throw new RangeError(`expected non-negative Knuts, got ${n}`);
  }
}

/**
 * Format an integer number of Knuts as `"7G 3S 12K"`.
 *
 * Edge cases:
 *  - `formatKnuts(0)` → `"0K"`
 *  - `formatKnuts(28)` → `"28K"`
 *  - `formatKnuts(29)` → `"1S"`
 *  - `formatKnuts(493)` → `"1G"`
 *  - compact mode uses the largest non-zero unit only
 *    (e.g. `7*493 + 50` → `"~7G"`).
 */
export function formatKnuts(n: number, opts: FormatOpts = {}): string {
  assertNonNegInteger(n);

  const galleons = Math.floor(n / KNUTS_PER_GALLEON);
  const remAfterG = n - galleons * KNUTS_PER_GALLEON;
  const sickles = Math.floor(remAfterG / KNUTS_PER_SICKLE);
  const knuts = remAfterG - sickles * KNUTS_PER_SICKLE;

  if (opts.compact) {
    if (galleons > 0) return `~${galleons}G`;
    if (sickles > 0) return `~${sickles}S`;
    return `~${knuts}K`;
  }

  if (n === 0) return '0K';
  const parts: string[] = [];
  if (galleons > 0) parts.push(`${galleons}G`);
  if (sickles > 0) parts.push(`${sickles}S`);
  if (knuts > 0) parts.push(`${knuts}K`);
  return parts.join(' ');
}

/** Inverse of `formatKnuts` (no compact mode). For round-trip property test. */
export function parseKnuts(s: string): number {
  if (!/^(?:\d+G(?: |$))?(?:\d+S(?: |$))?(?:\d+K)?$/.test(s.trim())) {
    if (s !== '0K') throw new SyntaxError(`unparseable Knuts string: ${s}`);
  }
  let total = 0;
  const re = /(\d+)([GSK])/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const v = parseInt(m[1]!, 10);
    switch (m[2]) {
      case 'G': total += v * KNUTS_PER_GALLEON; break;
      case 'S': total += v * KNUTS_PER_SICKLE; break;
      case 'K': total += v; break;
    }
  }
  return total;
}
